import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    Check,
    ChevronLeft,
    Database,
    Info,
    Loader2,
    Plus,
    Search,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { exportMeetingTranscriptToBrainBank } from "../../../../service/apiService";
import { useBrainBanks, BrainBankOption } from "../../../../hooks/useBrainBanks";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";
import CreateBrainBank from "../KnowledgeBaseComponents/CreateBrainBank";
import { BrainBankExport, BrainBankExportResult, MeetingSession } from "../../../../types";

interface SendToBrainBankModalProps {
    session: MeetingSession;
    onClose: () => void;
    onExported?: (result: BrainBankExportResult) => void;
    /** Opens speaker mapping so unnamed speakers can be fixed before sending. */
    onEditSpeakers?: () => void;
}

/** The copy already in the chosen BrainBank, when the server reports a clash. */
interface Conflict {
    knowledgeBankId: string;
    existing: BrainBankExport | null;
}

const formatDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

/**
 * Choose a BrainBank and send this session's transcript to it.
 *
 * The document is rendered server-side, so nothing clinical passes through
 * here — the dialog sends ids and shows what came back. Both warnings inform
 * rather than block: the therapist decides whether an unnamed or partially
 * captured transcript is worth indexing.
 */
const SendToBrainBankModal: React.FC<SendToBrainBankModalProps> = ({
    session,
    onClose,
    onExported,
    onEditSpeakers,
}) => {
    const { brainBanks, loading, error: loadError, refresh } = useBrainBanks();
    const { canCreateBrainBank } = useCreatePermissions();

    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [conflict, setConflict] = useState<Conflict | null>(null);
    // Non-null while the "no such BrainBank" create form is open; holds the
    // name the user typed, which pre-fills that form.
    const [creatingName, setCreatingName] = useState<string | null>(null);

    // Minted once per dialog, reused for every attempt including the Replace
    // confirmation: a double-click or a retry after a timeout returns the
    // first result instead of sending a second copy.
    const requestIdRef = useRef<string>(
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${session.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`
    );

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            // Not while the create form is open -- Escape there would throw
            // away a half-filled form.
            if (event.key === "Escape" && !sending && creatingName === null) onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose, sending, creatingName]);

    const speakersUnnamed = !session.speaker_mapping?.some(
        (mapping) => (mapping.participant_name || "").trim().length > 0
    );

    const gapCount = session.live_gap_count ?? 0;
    const hasGaps = session.live_transcript_complete === false || gapCount > 0;

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return brainBanks;
        return brainBanks.filter((bank) => bank.name.toLowerCase().includes(needle));
    }, [brainBanks, query]);

    const selected = brainBanks.find((bank) => bank.id === selectedId) || null;

    const sendTo = useCallback(
        async (knowledgeBaseId: string, replace: boolean) => {
            if (!knowledgeBaseId) return;

            setSending(true);
            setSendError(null);
            try {
                const result = await exportMeetingTranscriptToBrainBank(
                    session.id,
                    knowledgeBaseId,
                    requestIdRef.current,
                    replace
                );

                if (result.export_recorded === false) {
                    // The document IS in the BrainBank, but this session could
                    // not record it — so the copy will not appear in the list.
                    // Say so rather than showing a clean success.
                    toast.warning(
                        "Sent to the BrainBank, but this session couldn't record it. " +
                        "Sending again is safe — it won't create a second copy."
                    );
                } else if (result.status === "already_sent") {
                    toast.success("That BrainBank already has this transcript — nothing changed.");
                } else if (result.status === "replaced") {
                    toast.success(
                        result.previous_removed === false
                            ? "Sent. The older copy could not be removed — check the BrainBank."
                            : "Transcript replaced in the BrainBank."
                    );
                } else {
                    toast.success("Sent. The BrainBank is processing it now.");
                }

                onExported?.(result);
                onClose();
            } catch (err: any) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail;

                if (status === 409 && detail?.code === "export_exists") {
                    // Not an error the user needs to read — it is the Replace
                    // question, asked with the existing copy in hand.
                    setConflict({
                        knowledgeBankId: knowledgeBaseId,
                        existing: detail.export ?? null,
                    });
                    return;
                }

                setSendError(
                    typeof detail === "string"
                        ? detail
                        : detail?.message || "Couldn't send the transcript. Try again."
                );
            } finally {
                setSending(false);
            }
        },
        [onClose, onExported, session.id]
    );

    const send = useCallback(
        (replace: boolean) => {
            if (selectedId) sendTo(selectedId, replace);
        },
        [selectedId, sendTo]
    );

    if (creatingName !== null) {
        return (
            <CreateBrainBankView
                name={creatingName}
                onBack={() => setCreatingName(null)}
                onCreated={(created) => {
                    toast.success(`BrainBank "${created.name}" created.`);
                    // Straight on to the send the user asked for. Back on the
                    // list first, so a failure lands somewhere they can retry.
                    setSelectedId(created.index_id);
                    setCreatingName(null);
                    refresh();
                    sendTo(created.index_id, false);
                }}
            />
        );
    }

    if (conflict) {
        return (
            <ReplaceConfirmation
                brainBankName={selected?.name || "that BrainBank"}
                existing={conflict.existing}
                sending={sending}
                error={sendError}
                onCancel={() => {
                    setConflict(null);
                    setSendError(null);
                }}
                onConfirm={() => sendTo(conflict.knowledgeBankId, true)}
            />
        );
    }

    return (
        <Shell titleId="send-to-brain-bank-title" onClose={onClose} disabled={sending}>
            <div>
                <h3 id="send-to-brain-bank-title" className="text-lg font-semibold text-gray-900">
                    Send transcript to a BrainBank
                </h3>
                <p className="text-sm text-gray-500 mt-1 truncate">
                    {session.title || "Untitled Recording"}
                </p>
            </div>

            <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search BrainBanks"
                    aria-label="Search BrainBanks"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
            </div>

            <div className="max-h-56 overflow-y-auto -mx-1 px-1">
                {loading ? (
                    <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                        <span className="text-sm">Loading BrainBanks…</span>
                    </div>
                ) : loadError ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center">
                        <p className="text-sm text-gray-600">{loadError}</p>
                        <button
                            type="button"
                            onClick={refresh}
                            className="text-sm font-medium text-violet-600 hover:text-violet-700"
                        >
                            Try again
                        </button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-sm text-gray-500">
                            {query.trim()
                                ? `There's no BrainBank named "${query.trim()}".`
                                : "You don't have access to any BrainBanks yet. Ask an administrator to create one or share one with you."}
                        </p>
                        {query.trim() && canCreateBrainBank && (
                            <button
                                type="button"
                                onClick={() => setCreatingName(query.trim())}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Create BrainBank
                            </button>
                        )}
                    </div>
                ) : (
                    <ul className="space-y-2" role="radiogroup" aria-label="BrainBanks">
                        {filtered.map((bank) => (
                            <BrainBankRow
                                key={bank.id}
                                bank={bank}
                                selected={bank.id === selectedId}
                                onSelect={() => setSelectedId(bank.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {speakersUnnamed && (
                <Warning>
                    <span className="font-medium">Speakers aren't named yet.</span>{" "}
                    The transcript will read "Speaker 1" and "Speaker 2".
                    {onEditSpeakers && (
                        <button
                            type="button"
                            onClick={onEditSpeakers}
                            className="underline font-medium ml-1 whitespace-nowrap"
                        >
                            Name speakers first
                        </button>
                    )}
                </Warning>
            )}

            {hasGaps && (
                <Warning>
                    <span className="font-medium">
                        {gapCount > 0
                            ? `${gapCount} gap${gapCount === 1 ? "" : "s"} in this transcript.`
                            : "This transcript is incomplete."}
                    </span>{" "}
                    Parts of the session weren't captured live. The document will say so.
                </Warning>
            )}

            {selected && (
                <p className="text-sm text-gray-600 flex gap-2">
                    <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>
                        Anyone with access to{" "}
                        <span className="font-medium text-gray-900">{selected.name}</span> will be
                        able to read this transcript.
                    </span>
                </p>
            )}

            {sendError && (
                <p className="text-sm text-red-600" role="alert">
                    {sendError}
                </p>
            )}

            <div className="flex items-center justify-end gap-3 pt-1">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={sending}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => send(false)}
                    disabled={!selectedId || sending}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                    {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {sending ? "Sending…" : "Send to BrainBank"}
                </button>
            </div>
        </Shell>
    );
};

/** Shared overlay, matching the other meeting dialogs. */
const Shell: React.FC<{
    titleId: string;
    disabled?: boolean;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ titleId, disabled, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 relative flex flex-col gap-4"
        >
            <button
                type="button"
                onClick={onClose}
                disabled={disabled}
                aria-label="Close"
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
                <X className="w-5 h-5 text-gray-400" />
            </button>
            {children}
        </div>
    </div>
);

const Warning: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>{children}</p>
    </div>
);

const BrainBankRow: React.FC<{
    bank: BrainBankOption;
    selected: boolean;
    onSelect: () => void;
}> = ({ bank, selected, onSelect }) => (
    <li>
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            className={`w-full flex items-center gap-3 text-left border rounded-lg px-3 py-2.5 transition-colors ${
                selected
                    ? "border-violet-600 bg-violet-50 ring-1 ring-violet-600"
                    : "border-gray-200 hover:bg-gray-50"
            }`}
        >
            <Database
                className={`w-4 h-4 flex-shrink-0 ${
                    selected ? "text-violet-600" : "text-gray-400"
                }`}
            />
            <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900 truncate">
                    {bank.name}
                </span>
                {bank.file_count !== undefined && (
                    <span className="block text-xs text-gray-500">
                        {bank.file_count} document{bank.file_count === 1 ? "" : "s"}
                    </span>
                )}
            </span>
            {selected && <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />}
        </button>
    </li>
);

const ReplaceConfirmation: React.FC<{
    brainBankName: string;
    existing: BrainBankExport | null;
    sending: boolean;
    error: string | null;
    onCancel: () => void;
    onConfirm: () => void;
}> = ({ brainBankName, existing, sending, error, onCancel, onConfirm }) => (
    <Shell titleId="replace-brain-bank-title" onClose={onCancel} disabled={sending}>
        <h3 id="replace-brain-bank-title" className="text-lg font-semibold text-gray-900">
            Already in {brainBankName}
        </h3>
        <p className="text-sm text-gray-600">
            {existing?.exported_at
                ? `Sent ${formatDate(existing.exported_at)}. The transcript has changed since then.`
                : "This transcript has changed since it was last sent."}
        </p>
        {existing?.original_file_name && (
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 break-all font-mono">
                {existing.original_file_name}
            </p>
        )}
        <p className="text-sm text-gray-600">
            Replacing removes the older copy, so the BrainBank holds one version of this
            session.
        </p>
        {error && (
            <p className="text-sm text-red-600" role="alert">
                {error}
            </p>
        )}
        <div className="flex items-center justify-end gap-3 pt-1">
            <button
                type="button"
                onClick={onCancel}
                disabled={sending}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={onConfirm}
                disabled={sending}
                className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "Replacing…" : "Replace"}
            </button>
        </div>
    </Shell>
);

/**
 * The BrainBank creation form, opened from the "no such BrainBank" state with
 * the typed name already filled in. Wider than the send dialog because it is
 * the same form the BrainBanks screen shows.
 */
const CreateBrainBankView: React.FC<{
    name: string;
    onBack: () => void;
    onCreated: (created: { index_id: string; name: string; index_name: string }) => void;
}> = ({ name, onBack, onCreated }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-brain-bank-title"
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto p-6 relative"
        >
            <div className="flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="flex items-center text-sm font-medium text-violet-600 hover:text-violet-700"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                </button>
                <button
                    type="button"
                    onClick={onBack}
                    aria-label="Close"
                    className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <X className="w-5 h-5 text-gray-400" />
                </button>
            </div>

            <h3
                id="create-brain-bank-title"
                className="text-lg font-semibold text-gray-900 mt-2"
            >
                Create BrainBank
            </h3>
            <p className="text-sm text-gray-500 mt-1">
                Creating <span className="font-medium text-gray-900">{name}</span>. This
                transcript is sent to it as soon as it exists.
            </p>

            <CreateBrainBank
                initialName={name}
                onKnowledgeBaseChange={() => {}}
                onCreated={onCreated}
            />
        </div>
    </div>
);

export default SendToBrainBankModal;
